'use client';
import React from 'react';
import { createButton } from '@gluestack-ui/button';
import { tva } from '@gluestack-ui/nativewind-utils/tva';
import {
  withStyleContext,
  useStyleContext,
} from '@gluestack-ui/nativewind-utils/withStyleContext';
import { cssInterop } from 'nativewind';
import { ActivityIndicator, Pressable, Text, View } from 'react-native';
import type { VariantProps } from '@gluestack-ui/nativewind-utils';
import { PrimitiveIcon, UIIcon } from '@gluestack-ui/icon';
import { twMerge } from 'tailwind-merge';
import { GlassSurface, useLiquidGlass } from '@/components/ui/glass';

const SCOPE = 'BUTTON';

const Root = withStyleContext(Pressable, SCOPE);

const UIButton = createButton({
  Root: Root,
  Text,
  Group: View,
  Spinner: ActivityIndicator,
  Icon: UIIcon,
});

cssInterop(PrimitiveIcon, {
  className: {
    target: 'style',
    nativeStyleToProp: {
      height: true,
      width: true,
      fill: true,
      color: 'classNameColor',
      stroke: true,
    },
  },
});

/**
 * Fills, edges and hover states, held to WCAG in both themes and locked by the contrast tests.
 *
 * - The solid primary label is a fixed white, because `primary-500` is theme stable and
 *   `typography-0` turns near-black on it in dark. The ramp inverts, so the stock hover and
 *   press fills would lighten in dark and lose the white label; dark steps to 400 and 300
 *   instead, which are the same hex as 600 and 700 in light.
 * - The solid negative fill starts at `error-600`. `error-500` carries no label color that
 *   clears AA in light.
 * - Each action's border is its outline edge, and starts at the 500 stop: that is the
 *   lightest shade that clears 3:1 on the page in both themes. The 300 stop does not.
 * - Outline labels sit at 600 on the page and step to 700 on hover, whose `background-50`
 *   fill drops `primary-600` below AA in dark. `primary-500` misses AA on the page in dark,
 *   and `typography-500` misses it in light.
 */
const buttonStyle = tva({
  base: 'group/button rounded-full bg-primary-500 flex-row items-center justify-center data-[focus-visible=true]:web:outline-none data-[focus-visible=true]:web:ring-2 data-[disabled=true]:opacity-40 gap-2',
  variants: {
    action: {
      primary:
        'bg-primary-500 data-[hover=true]:bg-primary-600 data-[active=true]:bg-primary-700 dark:data-[hover=true]:bg-primary-400 dark:data-[active=true]:bg-primary-300 border-primary-500 data-[hover=true]:border-primary-600 data-[active=true]:border-primary-700 data-[focus-visible=true]:web:ring-indicator-info',
      secondary:
        'bg-secondary-500 border-secondary-500 data-[hover=true]:bg-secondary-600 data-[hover=true]:border-secondary-600 data-[active=true]:bg-secondary-700 data-[active=true]:border-secondary-700 data-[focus-visible=true]:web:ring-indicator-info',
      positive:
        'bg-success-500 border-success-500 data-[hover=true]:bg-success-600 data-[hover=true]:border-success-600 data-[active=true]:bg-success-700 data-[active=true]:border-success-700 data-[focus-visible=true]:web:ring-indicator-info',
      negative:
        'bg-error-600 border-error-500 data-[hover=true]:bg-error-700 data-[hover=true]:border-error-600 data-[active=true]:bg-error-800 data-[active=true]:border-error-700 data-[focus-visible=true]:web:ring-indicator-info',
      tertiary:
        'bg-tertiary-700 border-tertiary-700 data-[hover=true]:bg-tertiary-800 data-[hover=true]:border-tertiary-800 data-[active=true]:bg-tertiary-900 data-[active=true]:border-tertiary-900 data-[focus-visible=true]:web:ring-indicator-info',
      warning:
        'bg-warning-800 border-warning-800 data-[hover=true]:bg-warning-900 data-[hover=true]:border-warning-900 data-[active=true]:bg-warning-900 data-[active=true]:border-warning-900 data-[focus-visible=true]:web:ring-indicator-info',
      default:
        'bg-transparent data-[hover=true]:bg-background-50 data-[active=true]:bg-transparent',
    },
    variant: {
      link: 'px-0',
      outline:
        'bg-transparent border data-[hover=true]:bg-background-50 data-[active=true]:bg-transparent',
      solid: '',
    },

    size: {
      '2xs': 'px-2 h-6',
      xs: 'px-3.5 h-8',
      sm: 'px-4 h-9',
      md: 'px-5 h-10',
      lg: 'px-6 h-11',
      xl: 'px-7 h-12',
    },
  },
  compoundVariants: [
    {
      action: 'primary',
      variant: 'link',
      class:
        'px-0 bg-transparent data-[hover=true]:bg-transparent data-[active=true]:bg-transparent',
    },
    {
      action: 'secondary',
      variant: 'link',
      class:
        'px-0 bg-transparent data-[hover=true]:bg-transparent data-[active=true]:bg-transparent',
    },
    {
      action: 'positive',
      variant: 'link',
      class:
        'px-0 bg-transparent data-[hover=true]:bg-transparent data-[active=true]:bg-transparent',
    },
    {
      action: 'negative',
      variant: 'link',
      class:
        'px-0 bg-transparent data-[hover=true]:bg-transparent data-[active=true]:bg-transparent',
    },
    {
      action: 'tertiary',
      variant: 'link',
      class:
        'px-0 bg-transparent data-[hover=true]:bg-transparent data-[active=true]:bg-transparent',
    },
    {
      action: 'default',
      variant: 'link',
      class:
        'px-0 bg-transparent data-[hover=true]:bg-transparent data-[active=true]:bg-transparent',
    },
    {
      action: 'primary',
      variant: 'outline',
      class:
        'bg-transparent data-[hover=true]:bg-background-50 data-[active=true]:bg-transparent',
    },
    {
      action: 'secondary',
      variant: 'outline',
      class:
        'bg-transparent data-[hover=true]:bg-background-50 data-[active=true]:bg-transparent',
    },
    {
      action: 'positive',
      variant: 'outline',
      class:
        'bg-transparent data-[hover=true]:bg-background-50 data-[active=true]:bg-transparent',
    },
    {
      action: 'negative',
      variant: 'outline',
      class:
        'bg-transparent data-[hover=true]:bg-background-50 data-[active=true]:bg-transparent',
    },
    {
      action: 'tertiary',
      variant: 'outline',
      class:
        'bg-transparent border-tertiary-700 data-[hover=true]:bg-background-50 data-[hover=true]:border-tertiary-800 data-[active=true]:bg-transparent',
    },
    /**
     * The warning outline button, for an action on the amber status strip such as the away
     * banner's I'm back. Like the neutral outline it keeps a white fill so it reads as a control
     * on the tint; the `warning-800` edge and label clear AA on that fill in both themes.
     */
    {
      action: 'warning',
      variant: 'outline',
      class:
        'border-warning-800 bg-background-0 data-[active=true]:border-warning-900 data-[active=true]:bg-background-50 data-[hover=true]:border-warning-900 data-[hover=true]:bg-background-50',
    },
    /**
     * The neutral outline button, for a secondary action on a tinted strip such as a
     * muted banner. It keeps a white fill so it reads as a control on any tint, and the
     * edge does the separating: `outline-500` is the lightest stop that clears 3:1 on
     * the muted field. Hover and press darken the edge rather than the fill.
     */
    {
      action: 'default',
      variant: 'outline',
      class:
        'border-outline-500 bg-background-0 data-[active=true]:border-outline-700 data-[active=true]:bg-background-0 data-[hover=true]:border-outline-700 data-[hover=true]:bg-background-0',
    },
  ],
});

const buttonTextStyle = tva({
  base: 'text-typography-0 font-semibold web:select-none',
  parentVariants: {
    action: {
      primary:
        'text-primary-600 data-[hover=true]:text-primary-600 data-[active=true]:text-primary-700',
      secondary:
        'text-typography-500 data-[hover=true]:text-typography-600 data-[active=true]:text-typography-700',
      positive:
        'text-success-600 data-[hover=true]:text-success-600 data-[active=true]:text-success-700',
      negative:
        'text-error-600 data-[hover=true]:text-error-600 data-[active=true]:text-error-700',
      tertiary:
        'text-tertiary-700 data-[hover=true]:text-tertiary-700 data-[active=true]:text-tertiary-800',
      warning:
        'text-warning-800 data-[hover=true]:text-warning-900 data-[active=true]:text-warning-900',
    },
    variant: {
      link: 'data-[hover=true]:underline data-[active=true]:underline',
      outline: '',
      solid:
        'text-typography-0 data-[hover=true]:text-typography-0 data-[active=true]:text-typography-0',
    },
    size: {
      '2xs': 'text-xs',
      xs: 'text-xs',
      sm: 'text-sm',
      md: 'text-base',
      lg: 'text-lg',
      xl: 'text-xl',
    },
  },
  parentCompoundVariants: [
    {
      variant: 'solid',
      action: 'primary',
      class:
        'text-typography-white data-[hover=true]:text-typography-white data-[active=true]:text-typography-white',
    },
    {
      variant: 'solid',
      action: 'secondary',
      class:
        'text-typography-0 data-[hover=true]:text-typography-0 data-[active=true]:text-typography-0',
    },
    {
      variant: 'solid',
      action: 'positive',
      class:
        'text-typography-0 data-[hover=true]:text-typography-0 data-[active=true]:text-typography-0',
    },
    {
      variant: 'solid',
      action: 'negative',
      class:
        'text-typography-0 data-[hover=true]:text-typography-0 data-[active=true]:text-typography-0',
    },
    {
      variant: 'solid',
      action: 'tertiary',
      class:
        'text-typography-0 data-[hover=true]:text-typography-0 data-[active=true]:text-typography-0',
    },
    {
      variant: 'outline',
      action: 'primary',
      class:
        'text-primary-600 data-[hover=true]:text-primary-700 data-[active=true]:text-primary-700',
    },
    {
      variant: 'outline',
      action: 'secondary',
      class:
        'text-typography-600 data-[hover=true]:text-typography-700 data-[active=true]:text-typography-700',
    },
    {
      variant: 'outline',
      action: 'positive',
      class:
        'text-primary-600 data-[hover=true]:text-primary-700 data-[active=true]:text-primary-700',
    },
    {
      variant: 'outline',
      action: 'negative',
      class:
        'text-primary-600 data-[hover=true]:text-primary-700 data-[active=true]:text-primary-700',
    },
    {
      variant: 'outline',
      action: 'tertiary',
      class:
        'text-tertiary-700 data-[hover=true]:text-tertiary-800 data-[active=true]:text-tertiary-800',
    },
    {
      variant: 'outline',
      action: 'warning',
      class:
        'text-warning-800 data-[hover=true]:text-warning-900 data-[active=true]:text-warning-900',
    },
    {
      variant: 'outline',
      action: 'default',
      class:
        'text-typography-900 data-[hover=true]:text-typography-900 data-[active=true]:text-typography-900',
    },
  ],
});

const buttonIconStyle = tva({
  base: 'fill-none',
  parentVariants: {
    variant: {
      link: 'data-[hover=true]:underline data-[active=true]:underline',
      outline: '',
      solid:
        'text-typography-0 data-[hover=true]:text-typography-0 data-[active=true]:text-typography-0',
    },
    size: {
      '2xs': 'h-3 w-3',
      xs: 'h-3.5 w-3.5',
      sm: 'h-4 w-4',
      md: 'h-[18px] w-[18px]',
      lg: 'h-[18px] w-[18px]',
      xl: 'h-5 w-5',
    },
    action: {
      primary:
        'text-primary-600 data-[hover=true]:text-primary-600 data-[active=true]:text-primary-700',
      secondary:
        'text-typography-500 data-[hover=true]:text-typography-600 data-[active=true]:text-typography-700',
      positive:
        'text-success-600 data-[hover=true]:text-success-600 data-[active=true]:text-success-700',

      negative:
        'text-error-600 data-[hover=true]:text-error-600 data-[active=true]:text-error-700',
      tertiary:
        'text-tertiary-700 data-[hover=true]:text-tertiary-700 data-[active=true]:text-tertiary-800',
      warning:
        'text-warning-800 data-[hover=true]:text-warning-900 data-[active=true]:text-warning-900',
    },
  },
  parentCompoundVariants: [
    {
      variant: 'solid',
      action: 'primary',
      class:
        'text-typography-white data-[hover=true]:text-typography-white data-[active=true]:text-typography-white',
    },
    {
      variant: 'solid',
      action: 'secondary',
      class:
        'text-typography-0 data-[hover=true]:text-typography-0 data-[active=true]:text-typography-0',
    },
    {
      variant: 'solid',
      action: 'positive',
      class:
        'text-typography-0 data-[hover=true]:text-typography-0 data-[active=true]:text-typography-0',
    },
    {
      variant: 'solid',
      action: 'negative',
      class:
        'text-typography-0 data-[hover=true]:text-typography-0 data-[active=true]:text-typography-0',
    },
    {
      variant: 'solid',
      action: 'tertiary',
      class:
        'text-typography-0 data-[hover=true]:text-typography-0 data-[active=true]:text-typography-0',
    },
  ],
});

const buttonGroupStyle = tva({
  base: '',
  variants: {
    space: {
      'xs': 'gap-1',
      'sm': 'gap-2',
      'md': 'gap-3',
      'lg': 'gap-4',
      'xl': 'gap-5',
      '2xl': 'gap-6',
      '3xl': 'gap-7',
      '4xl': 'gap-8',
    },
    isAttached: {
      true: 'gap-0',
    },
    flexDirection: {
      'row': 'flex-row',
      'column': 'flex-col',
      'row-reverse': 'flex-row-reverse',
      'column-reverse': 'flex-col-reverse',
    },
  },
});

type ButtonVariant = NonNullable<VariantProps<typeof buttonStyle>['variant']>;

type IButtonProps = Omit<
  React.ComponentPropsWithoutRef<typeof UIButton>,
  'context'
> &
  Omit<VariantProps<typeof buttonStyle>, 'variant'> & {
    /**
     * `glass` is for a button that floats over content: a pinned footer, a
     * composer, a header. It is Liquid Glass on iOS 26 and a `solid` or
     * `outline` button everywhere else. Buttons that sit in the page, or inside
     * a sheet that is itself glass, keep the other variants.
     */
    variant?: ButtonVariant | 'glass';
    className?: string;
  };

/** The fill a glass button drops, so only the glass behind it shows. */
const GLASS_BUTTON_CLASS =
  'w-full border-0 bg-transparent data-[active=true]:bg-transparent data-[hover=true]:bg-transparent';

/**
 * The app's button. Every variant is a capsule on every platform.
 *
 * `variant="glass"` resolves to an ordinary variant first, `solid` for the
 * primary action and `outline` for any other, and that is all Android, web and
 * older iOS ever render. On iOS 26 the same button loses its fill and sits in
 * a glass capsule, tinted with the brand color when it is the primary action.
 * Label and icon colors come from the resolved variant either way.
 *
 * A glass button's `className` lands on the glass capsule, not on the
 * pressable inside it, so layout classes such as `flex-1` keep working. Set
 * its height with `size`, since a height class would resize only the capsule.
 */
const Button = React.forwardRef<
  React.ElementRef<typeof UIButton>,
  IButtonProps
>(
  (
    { className, variant = 'solid', size = 'md', action = 'primary', ...props },
    ref
  ) => {
    const hasGlass = useLiquidGlass();

    const isGlassVariant = variant === 'glass';

    const isPrimary = action === 'primary';

    const resolvedVariant: ButtonVariant = isGlassVariant
      ? isPrimary
        ? 'solid'
        : 'outline'
      : variant;

    if (isGlassVariant && hasGlass) {
      const isDisabled = Boolean(props.isDisabled ?? props.disabled);

      return (
        <GlassSurface
          className={twMerge('rounded-full', className)}
          tone={isPrimary ? 'primary' : 'neutral'}
          isDimmed={isDisabled}
          isInteractive={!isDisabled}>
          <UIButton
            ref={ref}
            {...props}
            className={twMerge(
              buttonStyle({ variant: resolvedVariant, size, action }),
              GLASS_BUTTON_CLASS
            )}
            context={{ variant: resolvedVariant, size, action }}
          />
        </GlassSurface>
      );
    }

    return (
      <UIButton
        ref={ref}
        {...props}
        className={buttonStyle({
          variant: resolvedVariant,
          size,
          action,
          class: className,
        })}
        context={{ variant: resolvedVariant, size, action }}
      />
    );
  }
);

type IButtonTextProps = React.ComponentPropsWithoutRef<typeof UIButton.Text> &
  VariantProps<typeof buttonTextStyle> & { className?: string };

const ButtonText = React.forwardRef<
  React.ElementRef<typeof UIButton.Text>,
  IButtonTextProps
>(({ className, variant, size, action, ...props }, ref) => {
  const {
    variant: parentVariant,
    size: parentSize,
    action: parentAction,
  } = useStyleContext(SCOPE);

  return (
    <UIButton.Text
      ref={ref}
      {...props}
      className={buttonTextStyle({
        parentVariants: {
          variant: parentVariant,
          size: parentSize,
          action: parentAction,
        },
        variant,
        size,
        action,
        class: className,
      })}
    />
  );
});

const ButtonSpinner = UIButton.Spinner as React.ComponentType<
  React.ComponentProps<typeof ActivityIndicator>
>;

type IButtonIcon = React.ComponentPropsWithoutRef<typeof UIButton.Icon> &
  VariantProps<typeof buttonIconStyle> & {
    className?: string | undefined;
    as?: React.ElementType;
    height?: number;
    width?: number;
  };

const ButtonIcon = React.forwardRef<
  React.ElementRef<typeof UIButton.Icon>,
  IButtonIcon
>(({ className, size, ...props }, ref) => {
  const {
    variant: parentVariant,
    size: parentSize,
    action: parentAction,
  } = useStyleContext(SCOPE);

  if (typeof size === 'number') {
    return (
      <UIButton.Icon
        ref={ref}
        {...props}
        className={buttonIconStyle({ class: className })}
        size={size}
      />
    );
  } else if (
    (props.height !== undefined || props.width !== undefined) &&
    size === undefined
  ) {
    return (
      <UIButton.Icon
        ref={ref}
        {...props}
        className={buttonIconStyle({ class: className })}
      />
    );
  }
  return (
    <UIButton.Icon
      {...props}
      className={buttonIconStyle({
        parentVariants: {
          size: parentSize,
          variant: parentVariant,
          action: parentAction,
        },
        size,
        class: className,
      })}
      ref={ref}
    />
  );
});

type IButtonGroupProps = React.ComponentPropsWithoutRef<typeof UIButton.Group> &
  VariantProps<typeof buttonGroupStyle>;

const ButtonGroup = React.forwardRef<
  React.ElementRef<typeof UIButton.Group>,
  IButtonGroupProps
>(
  (
    {
      className,
      space = 'md',
      isAttached = false,
      flexDirection = 'column',
      ...props
    },
    ref
  ) => {
    return (
      <UIButton.Group
        className={buttonGroupStyle({
          class: className,
          space,
          isAttached,
          flexDirection,
        })}
        {...props}
        ref={ref}
      />
    );
  }
);

Button.displayName = 'Button';
ButtonText.displayName = 'ButtonText';
ButtonSpinner.displayName = 'ButtonSpinner';
ButtonIcon.displayName = 'ButtonIcon';
ButtonGroup.displayName = 'ButtonGroup';

export { Button, ButtonText, ButtonSpinner, ButtonIcon, ButtonGroup };
